import React, { useState, useEffect } from 'react';
import { 
  Palette, 
  Image, 
  Type, 
  Code, 
  Crown, 
  Save, 
  RotateCcw,
  Eye,
  Download,
  Upload
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const PremiumThemeSettings = ({ user, onSave, onReset }) => {
  const navigate = useNavigate();
  
  // Premium Tema Ayarları
  const [themeSettings, setThemeSettings] = useState({
    // Arka Plan Ayarları
    backgroundType: 'gradient', // 'gradient', 'image', 'solid', 'pattern'
    backgroundColor: '#1e3a8a',
    backgroundGradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)',
    backgroundImage: '',
    backgroundPattern: 'none', // 'dots', 'grid', 'waves', 'geometric', 'none'
    backgroundOpacity: 0.9,
    
    // Renk Paleti
    primaryColor: '#1e3a8a',
    secondaryColor: '#3b82f6',
    accentColor: '#fbbf24',
    textColor: '#ffffff',
    cardColor: 'rgba(30, 58, 138, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.3)',
    
    // Yazı Tipi Ayarları
    fontFamily: 'Inter', // 'Inter', 'Roboto', 'Poppins', 'Montserrat', 'Open Sans', 'Lato'
    fontSize: 'medium', // 'small', 'medium', 'large', 'xlarge'
    fontWeight: 'normal', // 'light', 'normal', 'medium', 'semibold', 'bold'
    lineHeight: 'normal', // 'tight', 'normal', 'relaxed', 'loose'
    letterSpacing: 'normal', // 'tight', 'normal', 'wide'
    
    // Header Ayarları
    headerStyle: 'gradient', // 'gradient', 'solid', 'glass', 'minimal'
    headerColor: '#1e3a8a',
    headerGradient: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 50%, #1e3a8a 100%)',
    headerOpacity: 0.9,
    headerBlur: true,
    
    // Kart Ayarları
    cardStyle: 'glass', // 'glass', 'solid', 'outline', 'minimal'
    cardOpacity: 0.1,
    cardBlur: true,
    cardBorder: true,
    cardShadow: true,
    cardRadius: 'large', // 'none', 'small', 'medium', 'large', 'xl'
    
    // Buton Ayarları
    buttonStyle: 'gradient', // 'gradient', 'solid', 'outline', 'minimal'
    buttonRadius: 'medium', // 'none', 'small', 'medium', 'large', 'full'
    buttonShadow: true,
    buttonHover: true,
    
    // Animasyon Ayarları
    animations: true,
    animationSpeed: 'normal', // 'slow', 'normal', 'fast'
    hoverEffects: true,
    transitions: true,
    
    // Özel Efektler
    particles: false,
    particleType: 'dots', // 'dots', 'stars', 'snow', 'hearts'
    particleColor: '#fbbf24',
    particleCount: 50,
    
    // Gölge ve Efektler
    shadows: true,
    shadowIntensity: 'medium', // 'light', 'medium', 'strong'
    glowEffects: false,
    glowColor: '#3b82f6',
    
    // Özel CSS
    customCSS: ''
  });

  const [previewMode, setPreviewMode] = useState(false);

  // Tema ayarlarını uygula
  const applyThemeSettings = () => {
    const root = document.documentElement;
    
    // CSS değişkenlerini ayarla
    root.style.setProperty('--theme-primary', themeSettings.primaryColor);
    root.style.setProperty('--theme-secondary', themeSettings.secondaryColor);
    root.style.setProperty('--theme-accent', themeSettings.accentColor);
    root.style.setProperty('--theme-text', themeSettings.textColor);
    root.style.setProperty('--theme-card', themeSettings.cardColor);
    root.style.setProperty('--theme-border', themeSettings.borderColor);
    
    // Font ayarları
    root.style.setProperty('--theme-font-family', themeSettings.fontFamily);
    root.style.setProperty('--theme-font-size', themeSettings.fontSize);
    root.style.setProperty('--theme-font-weight', themeSettings.fontWeight);
    root.style.setProperty('--theme-line-height', themeSettings.lineHeight);
    root.style.setProperty('--theme-letter-spacing', themeSettings.letterSpacing);
    
    // Arka plan ayarları
    if (themeSettings.backgroundType === 'gradient') {
      root.style.setProperty('--theme-background', themeSettings.backgroundGradient);
    } else if (themeSettings.backgroundType === 'solid') {
      root.style.setProperty('--theme-background', themeSettings.backgroundColor);
    } else if (themeSettings.backgroundType === 'image') {
      root.style.setProperty('--theme-background', `url(${themeSettings.backgroundImage})`);
    }
    
    // Özel CSS uygula
    if (themeSettings.customCSS) {
      const styleId = 'custom-theme-css';
      let customStyle = document.getElementById(styleId);
      if (!customStyle) {
        customStyle = document.createElement('style');
        customStyle.id = styleId;
        document.head.appendChild(customStyle);
      }
      customStyle.textContent = themeSettings.customCSS;
    }
  };

  // Tema ayarlarını kaydet
  const saveThemeSettings = () => {
    localStorage.setItem('premium-theme-settings', JSON.stringify(themeSettings));
    applyThemeSettings();
    if (onSave) onSave(themeSettings);
    toast.success('Tema ayarları kaydedildi!');
  };

  // Tema ayarlarını yükle
  const loadThemeSettings = () => {
    const saved = localStorage.getItem('premium-theme-settings');
    if (saved) {
      setThemeSettings(JSON.parse(saved));
    }
  };

  // Tema ayarlarını sıfırla
  const resetThemeSettings = () => {
    const defaultSettings = {
      backgroundType: 'gradient',
      backgroundColor: '#1e3a8a',
      backgroundGradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)',
      backgroundImage: '',
      backgroundPattern: 'none',
      backgroundOpacity: 0.9,
      primaryColor: '#1e3a8a',
      secondaryColor: '#3b82f6',
      accentColor: '#fbbf24',
      textColor: '#ffffff',
      cardColor: 'rgba(30, 58, 138, 0.1)',
      borderColor: 'rgba(59, 130, 246, 0.3)',
      fontFamily: 'Inter',
      fontSize: 'medium',
      fontWeight: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      headerStyle: 'gradient',
      headerColor: '#1e3a8a',
      headerGradient: 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 50%, #1e3a8a 100%)',
      headerOpacity: 0.9,
      headerBlur: true,
      cardStyle: 'glass',
      cardOpacity: 0.1,
      cardBlur: true,
      cardBorder: true,
      cardShadow: true,
      cardRadius: 'large',
      buttonStyle: 'gradient',
      buttonRadius: 'medium',
      buttonShadow: true,
      buttonHover: true,
      animations: true,
      animationSpeed: 'normal',
      hoverEffects: true,
      transitions: true,
      particles: false,
      particleType: 'dots',
      particleColor: '#fbbf24',
      particleCount: 50,
      shadows: true,
      shadowIntensity: 'medium',
      glowEffects: false,
      glowColor: '#3b82f6',
      customCSS: ''
    };
    setThemeSettings(defaultSettings);
    if (onReset) onReset(defaultSettings);
    toast.success('Tema ayarları sıfırlandı!');
  };

  // Tema ayarlarını dışa aktar
  const exportThemeSettings = () => {
    const dataStr = JSON.stringify(themeSettings, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = 'gokturk-theme-settings.json';
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast.success('Tema ayarları dışa aktarıldı!');
  };

  // Tema ayarlarını içe aktar
  const importThemeSettings = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedSettings = JSON.parse(e.target.result);
          setThemeSettings(importedSettings);
          toast.success('Tema ayarları içe aktarıldı!');
        } catch (error) {
          toast.error('Geçersiz tema dosyası!');
        }
      };
      reader.readAsText(file);
    }
  };

  // Bileşen yüklendiğinde tema ayarlarını yükle
  useEffect(() => {
    if (user?.is_premium) {
      loadThemeSettings();
    }
  }, [user?.is_premium]);

  // Tema ayarları değiştiğinde önizleme uygula
  useEffect(() => {
    if (previewMode) {
      applyThemeSettings();
    }
  }, [themeSettings, previewMode]);

  if (!user?.is_premium) {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900 dark:to-orange-900 rounded-lg p-6 border border-amber-200 dark:border-amber-700">
        <div className="flex items-center space-x-3 mb-4">
          <Crown className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          <h3 className="text-xl font-bold text-amber-800 dark:text-amber-200">Premium Tema Özelleştirme</h3>
        </div>
        <p className="text-amber-700 dark:text-amber-300 mb-4">
          Detaylı tema özelleştirme özelliklerine erişmek için Premium üyelik gereklidir.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
            <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">🎨 Renk Paleti</h4>
            <p className="text-sm text-amber-600 dark:text-amber-400">Özel renkler ve gradientler</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
            <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">🔤 Yazı Tipleri</h4>
            <p className="text-sm text-amber-600 dark:text-amber-400">Font ailesi ve boyut ayarları</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-amber-200 dark:border-amber-700">
            <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">🖼️ Arka Planlar</h4>
            <p className="text-sm text-amber-600 dark:text-amber-400">Gradient, resim ve desenler</p>
          </div>
        </div>
        <button 
          onClick={() => navigate('/premium')}
          className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-200"
        >
          Premium'a Yükselt
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Kontrol Paneli */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Palette className="w-5 h-5 mr-2" />
            Premium Tema Ayarları
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                previewMode 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <Eye className="w-4 h-4 mr-1 inline" />
              Önizleme
            </button>
            <button
              onClick={exportThemeSettings}
              className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors"
            >
              <Download className="w-4 h-4 mr-1 inline" />
              Dışa Aktar
            </button>
            <label className="px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors cursor-pointer">
              <Upload className="w-4 h-4 mr-1 inline" />
              İçe Aktar
              <input
                type="file"
                accept=".json"
                onChange={importThemeSettings}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Arka Plan Ayarları */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Image className="w-5 h-5 mr-2" />
          Arka Plan Ayarları
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Arka Plan Türü
            </label>
            <select
              value={themeSettings.backgroundType}
              onChange={(e) => setThemeSettings({...themeSettings, backgroundType: e.target.value})}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="gradient">Gradient</option>
              <option value="solid">Düz Renk</option>
              <option value="image">Resim</option>
              <option value="pattern">Desen</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Şeffaflık
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={themeSettings.backgroundOpacity}
              onChange={(e) => setThemeSettings({...themeSettings, backgroundOpacity: parseFloat(e.target.value)})}
              className="w-full"
            />
            <span className="text-sm text-gray-500">{Math.round(themeSettings.backgroundOpacity * 100)}%</span>
          </div>
        </div>
        
        {themeSettings.backgroundType === 'gradient' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Gradient CSS
            </label>
            <textarea
              value={themeSettings.backgroundGradient}
              onChange={(e) => setThemeSettings({...themeSettings, backgroundGradient: e.target.value})}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
              rows="2"
              placeholder="linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)"
            />
          </div>
        )}
        
        {themeSettings.backgroundType === 'solid' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Arka Plan Rengi
            </label>
            <input
              type="color"
              value={themeSettings.backgroundColor}
              onChange={(e) => setThemeSettings({...themeSettings, backgroundColor: e.target.value})}
              className="w-20 h-10 border border-gray-300 dark:border-gray-600 rounded-lg"
            />
          </div>
        )}
        
        {themeSettings.backgroundType === 'image' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Resim URL'si
            </label>
            <input
              type="url"
              value={themeSettings.backgroundImage}
              onChange={(e) => setThemeSettings({...themeSettings, backgroundImage: e.target.value})}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="https://example.com/image.jpg"
            />
          </div>
        )}
      </div>

      {/* Renk Paleti */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Palette className="w-5 h-5 mr-2" />
          Renk Paleti
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ana Renk
            </label>
            <input
              type="color"
              value={themeSettings.primaryColor}
              onChange={(e) => setThemeSettings({...themeSettings, primaryColor: e.target.value})}
              className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              İkincil Renk
            </label>
            <input
              type="color"
              value={themeSettings.secondaryColor}
              onChange={(e) => setThemeSettings({...themeSettings, secondaryColor: e.target.value})}
              className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Vurgu Rengi
            </label>
            <input
              type="color"
              value={themeSettings.accentColor}
              onChange={(e) => setThemeSettings({...themeSettings, accentColor: e.target.value})}
              className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Yazı Tipi Ayarları */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Type className="w-5 h-5 mr-2" />
          Yazı Tipi Ayarları
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Font Ailesi
            </label>
            <select
              value={themeSettings.fontFamily}
              onChange={(e) => setThemeSettings({...themeSettings, fontFamily: e.target.value})}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="Inter">Inter</option>
              <option value="Roboto">Roboto</option>
              <option value="Poppins">Poppins</option>
              <option value="Montserrat">Montserrat</option>
              <option value="Open Sans">Open Sans</option>
              <option value="Lato">Lato</option>
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Font Boyutu
            </label>
            <select
              value={themeSettings.fontSize}
              onChange={(e) => setThemeSettings({...themeSettings, fontSize: e.target.value})}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="small">Küçük</option>
              <option value="medium">Orta</option>
              <option value="large">Büyük</option>
              <option value="xlarge">Çok Büyük</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Font Kalınlığı
            </label>
            <select
              value={themeSettings.fontWeight}
              onChange={(e) => setThemeSettings({...themeSettings, fontWeight: e.target.value})}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="light">İnce</option>
              <option value="normal">Normal</option>
              <option value="medium">Orta</option>
              <option value="semibold">Yarı Kalın</option>
              <option value="bold">Kalın</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Satır Yüksekliği
            </label>
            <select
              value={themeSettings.lineHeight}
              onChange={(e) => setThemeSettings({...themeSettings, lineHeight: e.target.value})}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="tight">Sıkı</option>
              <option value="normal">Normal</option>
              <option value="relaxed">Rahat</option>
              <option value="loose">Gevşek</option>
            </select>
          </div>
        </div>
      </div>

      {/* Özel CSS */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Code className="w-5 h-5 mr-2" />
          Özel CSS
        </h3>
        <textarea
          value={themeSettings.customCSS}
          onChange={(e) => setThemeSettings({...themeSettings, customCSS: e.target.value})}
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
          rows="8"
          placeholder="/* Özel CSS kodlarınızı buraya yazın */
.custom-element {
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}"
        />
      </div>

      {/* Kaydet Butonu */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={resetThemeSettings}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Sıfırla
        </button>
        <button
          onClick={saveThemeSettings}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200 font-semibold flex items-center"
        >
          <Save className="w-4 h-4 mr-2" />
          Kaydet
        </button>
      </div>
    </div>
  );
};

export default PremiumThemeSettings;
